import React, { useState, useCallback, ChangeEvent, useEffect, useMemo } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import { User, UserRole, UserPoints, PointsTransaction, PromptType, getPromptTypeKoreanName } from "common/models/types";
import { ROLE_NAMES } from "common/constants/roles";
import Text from "components/atoms/Text";
import Button from "components/atoms/Button";
import Input from "components/atoms/Input";
import Icon from "components/atoms/Icon";
import Tag from "components/atoms/Tag";
import Switch from "components/atoms/Switch";
import RadioButton from "components/atoms/RadioButton";
import ToggleButton from "components/atoms/ToggleButton";
import { saveUser, deleteUser } from "api/userService";
import { fetchPointsTransactionsByUserId, adminGrantPoints } from "api/pointsService";
import { ConfirmManager } from "components/organisms/CommonUI/ConfirmPopup/ConfirmManager";
import { WaitingManager } from "components/organisms/CommonUI/WaitingPopup/WaitingManager";
import { ToastManager } from "components/organisms/CommonUI/ToastPopup/ToastManager";
import { log } from "utils/LogUtil";
import { LogLevel } from "utils/LogUtil";

interface UserInfoProps {
  user: User | null;
  onEdit?: () => void;
  onUpdate?: (user: User) => void;
  onSelect?: (user: User | null) => void;
  onReload?: (userId: string) => void;
  onDelete?: (userId: string) => void;
  isLoading?: boolean;
  onResetPassword?: (user: User) => void;
}

// 컨테이너 스타일
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 500px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
`;

// 헤더 스타일
const Header = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

// 내용 영역 스타일
const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;

// 편집 영역 스타일
const EditContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  flex: 1;
  overflow-y: auto;
`;

// 빈 상태 메시지 스타일
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 32px 16px;
  text-align: center;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

// 폼 그룹 스타일
const FormGroup = styled.div`
  margin-bottom: 24px;
`;

// 폼 라벨 스타일
const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
`;

// 액션 버튼 영역 스타일
const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px;
  border-top: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
`;

// 메타 정보 스타일
const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
  padding: 12px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-radius: 8px;
`;

// 메타 항목 스타일
const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 150px;
`;

// 역할 선택 컨테이너
const RoleSelector = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  max-height: 120px;
  overflow-y: auto;
  padding: 4px;
`;

// 역할 옵션
const RoleOption = styled.div<{ isSelected: boolean }>`
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  cursor: pointer;
  background-color: ${({ isSelected }) =>
    isSelected ? COLOR_SYSTEM.get("Blue")?.[100] : COLOR_SYSTEM.get("Gray")?.[50]};
  color: ${({ isSelected }) =>
    isSelected ? COLOR_SYSTEM.get("Blue")?.[700] : COLOR_SYSTEM.get("Gray")?.[700]};
  border: 1px solid
    ${({ isSelected }) =>
      isSelected ? COLOR_SYSTEM.get("Blue")?.[300] : COLOR_SYSTEM.get("Gray")?.[200]};

  &:hover {
    background-color: ${({ isSelected }) =>
      isSelected ? COLOR_SYSTEM.get("Blue")?.[100] : COLOR_SYSTEM.get("Gray")?.[100]};
  }
`;

// 텍스트 영역 스타일
const TextAreaInput = styled.textarea`
  width: 100%;
  min-height: 80px;
  max-height: 200px;
  padding: 12px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[300]};
  border-radius: 4px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  overflow-y: auto;

  &:focus {
    border-color: ${COLOR_SYSTEM.get("Blue")?.[400]};
    outline: none;
  }
`;

// 정보 섹션 스타일
const InfoSection = styled.div`
  margin-bottom: 24px;
`;

// 정보 섹션 헤더 스타일
const SectionHeader = styled.div`
  margin-bottom: 16px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  padding-bottom: 8px;
`;

// 정보 항목 스타일
const InfoItem = styled.div`
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
`;

// 역할 태그 컨테이너
const RoleTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

// 라디오 옵션 그룹 스타일
const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// 포인트 정보 카드 스타일
const PointsCard = styled.div`
  background-color: ${COLOR_SYSTEM.get("Blue")?.[50]};
  border: 1px solid ${COLOR_SYSTEM.get("Blue")?.[200]};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`;

// 포인트 그리드 스타일
const PointsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 16px;
`;

// 포인트 아이템 스타일
const PointsItem = styled.div`
  text-align: center;
`;

// 포인트 지급 폼 스타일
const GrantPointsForm = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
  margin-top: 16px;
`;

// 포인트 거래 내역 리스트 스타일
const TransactionsList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 4px;
  background-color: white;
`;

// 거래 내역 아이템 스타일
const TransactionItem = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:last-child {
    border-bottom: none;
  }
`;

// 거래 정보 스타일
const TransactionInfo = styled.div`
  flex: 1;
`;

// 거래 금액 스타일
const TransactionAmount = styled.div<{ isPositive: boolean }>`
  font-weight: bold;
  color: ${({ isPositive }) =>
    isPositive ? COLOR_SYSTEM.get("Green")?.[600] : COLOR_SYSTEM.get("Red")?.[600]};
`;

// 버튼 그룹 스타일
const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

// 커스텀 역할 태그 컨테이너
const CustomRoleTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

// 커스텀 역할 태그
const CustomRoleTag = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 16px;
  background-color: ${COLOR_SYSTEM.get('Blue')?.[50]};
  border: 1px solid ${COLOR_SYSTEM.get('Blue')?.[200]};

  svg {
    color: ${COLOR_SYSTEM.get('Gray')?.[500]};
    transition: color 0.2s;
    cursor: pointer;

    &:hover {
      color: ${COLOR_SYSTEM.get('Red')?.[500]};
    }
  }
`;

// 비밀번호 생성 옵션 타입
type PasswordOption = "auto" | "manual";

// 역할 한글 이름 매핑 - roles.ts에서 가져오도록 변경
// ROLE_NAMES는 common/constants/roles.ts에서 import됨

const UserInfo: React.FC<UserInfoProps> = ({
  user,
  onEdit,
  onUpdate,
  onSelect,
  onReload,
  onDelete,
  isLoading,
  onResetPassword,
}) => {
  // 편집 모드 상태
  const [isEditing, setIsEditing] = useState<boolean>(false);
  // 편집 중인 사용자 상태
  const [editingUser, setEditingUser] = useState<User | null>(null);
  // 저장 중 상태
  const [isSaving, setIsSaving] = useState<boolean>(false);
  // 비밀번호 설정 옵션
  const [passwordType, setPasswordType] = useState<"auto" | "manual">("auto");
  // 직접 입력한 비밀번호
  const [manualPassword, setManualPassword] = useState<string>("");
  // 삭제 중 상태
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  // 포인트 관련 상태
  const [userPoints, setUserPoints] = useState<UserPoints | null>(null);
  const [pointsTransactions, setPointsTransactions] = useState<PointsTransaction[]>([]);
  const [isLoadingPoints, setIsLoadingPoints] = useState<boolean>(false);
  const [grantPointsAmount, setGrantPointsAmount] = useState<string>("");
  const [grantDescription, setGrantDescription] = useState<string>("");
  const [isGrantingPoints, setIsGrantingPoints] = useState<boolean>(false);
  // 커스텀 역할 입력 상태
  const [customRoleInput, setCustomRoleInput] = useState<string>("");

  // Preset(UserRole enum에 정의된 역할)과 커스텀 역할 분리
  const presetRoles = Object.values(UserRole);
  const customRoles = useMemo(() => {
    return editingUser?.roles?.filter(role =>
      !presetRoles.includes(role as UserRole)
    ) || [];
  }, [editingUser?.roles]);

  // 새 사용자인지 확인
  const isNewUser = user && !user.id;

  // 포인트 정보 로드
  const loadUserPointsData = useCallback(async () => {
    if (!user?.id) return;

    try {
      setIsLoadingPoints(true);

      // 사용자 포인트 정보 (user.userPoints에서 가져오기)
      if (user.userPoints) {
        setUserPoints(user.userPoints);
      }

      // 포인트 거래 내역 조회 (최근 10건)
      const transactions = await fetchPointsTransactionsByUserId(user.id, {
        limit: 10,
        page: 1,
      });
      setPointsTransactions(transactions);
    } catch (error) {
      log(LogLevel.ERROR, "포인트 정보 로드 실패:", error);
    } finally {
      setIsLoadingPoints(false);
    }
  }, [user?.id, user?.userPoints]);

  // 포인트 지급 처리
  const handleGrantPoints = useCallback(async () => {
    if (!user?.id || !grantPointsAmount || isGrantingPoints) return;

    const points = parseInt(grantPointsAmount);
    if (isNaN(points) || points <= 0) {
      ToastManager.show({
        content: "올바른 포인트 값을 입력해주세요.",
        duration: 3000,
      });
      return;
    }

    try {
      setIsGrantingPoints(true);
      WaitingManager.show({
        message: "포인트를 지급하는 중입니다...",
      });

      await adminGrantPoints({
        userId: user.id,
        points,
        description: grantDescription || "관리자 포인트 지급",
      });

      ToastManager.show({
        content: `${points}P가 성공적으로 지급되었습니다.`,
        duration: 2000,
      });

      // 입력 필드 초기화
      setGrantPointsAmount("");
      setGrantDescription("");

      // 포인트 정보 다시 로드
      await loadUserPointsData();

      // 사용자 정보 다시 로드 (포인트 잔액 업데이트)
      if (onReload && user.id) {
        onReload(user.id);
      }
    } catch (error) {
      log(LogLevel.ERROR, "포인트 지급 실패:", error);
      ToastManager.show({
        content: "포인트 지급에 실패했습니다.",
        duration: 3000,
      });
    } finally {
      setIsGrantingPoints(false);
      WaitingManager.hide();
    }
  }, [
    user?.id,
    grantPointsAmount,
    grantDescription,
    isGrantingPoints,
    loadUserPointsData,
    onReload,
  ]);

  // 컴포넌트 마운트 또는 사용자 변경 시 체크
  React.useEffect(() => {
    // 새 사용자가 선택되면 자동으로 편집 모드로 전환
    if (isNewUser && !isEditing) {
      setEditingUser({ ...user });
      setIsEditing(true);
      // 비밀번호 옵션 기본값 설정
      setPasswordType("auto");
      setManualPassword("");
    }
  }, [user, isNewUser, isEditing]);

  // 사용자 변경 시 포인트 데이터 로드
  useEffect(() => {
    if (user?.id && !isNewUser) {
      loadUserPointsData();
    }
  }, [user?.id, isNewUser, loadUserPointsData]);

  // 편집 시작 핸들러
  const handleStartEdit = useCallback(() => {
    if (user) {
      setEditingUser({ ...user });
      setIsEditing(true);
      // 기존 사용자 편집 시에는 비밀번호 옵션 초기화
      setPasswordType("auto");
      setManualPassword("");
    }
  }, [user]);

  // 편집 취소 핸들러
  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditingUser(null);
    setPasswordType("auto");
    setManualPassword("");

    // 새 사용자 생성 중 취소한 경우 선택 해제
    if (isNewUser && onSelect) {
      onSelect(null);
    }
  }, [isNewUser, onSelect]);

  // 삭제 모달 열기 핸들러
  const handleOpenDeleteModal = useCallback(() => {
    if (user) {
      ConfirmManager.show({
        title: "사용자 삭제",
        body: (
          <>
            <Text textType="Body1">
              {user.name} ({user.email}) 사용자를 삭제하시겠습니까?
              <br />
            </Text>
            <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[600]} style={{ marginTop: 8 }}>
              삭제된 사용자는 복구할 수 없습니다.
            </Text>
          </>
        ),
        buttons: [
          <Button
            key="cancel"
            buttonType="button"
            color="Secondary"
            size="Medium"
            type="Text"
            variant="Outlined"
            onClick={() => ConfirmManager.hide()}
            disabled={isDeleting}
          >
            취소
          </Button>,
          <Button
            key="confirm"
            buttonType="button"
            color="Negative"
            size="Medium"
            type="Text"
            variant="Contained"
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? "삭제 중..." : "삭제"}
          </Button>,
        ],
      });
    }
  }, [user, isDeleting]);

  // 삭제 확인 핸들러
  const handleDeleteConfirm = useCallback(async () => {
    if (user?.id) {
      try {
        setIsDeleting(true);
        // 대기 팝업 표시
        WaitingManager.show({
          message: "사용자를 삭제하는 중입니다...",
        });

        // 사용자 삭제 API 호출
        await deleteUser(user.id);

        // 삭제 완료 후 모달 닫기
        ConfirmManager.hide();
        WaitingManager.hide();

        // 성공 토스트 메시지 표시
        ToastManager.show({
          content: `${user.name} 사용자가 삭제되었습니다.`,
          duration: 3000,
        });

        // 상위 컴포넌트에 삭제 알림
        if (onDelete) {
          onDelete(user.id);
        }

        // 선택 해제
        if (onSelect) {
          onSelect(null);
        }
      } catch (error) {
        log(LogLevel.ERROR, "사용자 삭제 실패:", error);
        // 에러 토스트 메시지 표시
        ToastManager.show({
          content: "사용자 삭제에 실패했습니다.",
          duration: 3000,
        });
        WaitingManager.hide();
      } finally {
        setIsDeleting(false);
      }
    }
  }, [user?.id, onDelete, onSelect]);

  // 입력값 변경 핸들러
  const handleInputChange = useCallback(
    (field: keyof User, value: any) => {
      if (editingUser) {
        setEditingUser((prev) => (prev ? { ...prev, [field]: value } : null));
      }
    },
    [editingUser]
  );

  // 역할 토글 핸들러 (Preset 역할만 처리)
  const handleRoleToggle = useCallback(
    (role: string) => {
      if (editingUser) {
        const roles = [...(editingUser.roles || [])];
        const roleIndex = roles.indexOf(role);

        if (roleIndex === -1) {
          // 역할 추가
          roles.push(role);
        } else {
          // 역할 제거 (최소 1개 역할은 유지)
          if (roles.length > 1) {
            roles.splice(roleIndex, 1);
          }
        }

        setEditingUser((prev) => (prev ? { ...prev, roles } : null));
      }
    },
    [editingUser]
  );

  // 커스텀 역할 추가 핸들러
  const handleAddCustomRole = useCallback(() => {
    const trimmedRole = customRoleInput.trim();
    if (!trimmedRole || !editingUser) return;

    const roles = [...(editingUser.roles || [])];
    if (!roles.includes(trimmedRole)) {
      roles.push(trimmedRole);
      setEditingUser((prev) => (prev ? { ...prev, roles } : null));
      setCustomRoleInput("");

      ToastManager.show({
        content: `"${trimmedRole}" 역할이 추가되었습니다.`,
        duration: 2000,
      });
    } else {
      ToastManager.show({
        content: `이미 추가된 역할입니다.`,
        duration: 2000,
      });
    }
  }, [customRoleInput, editingUser]);

  // 커스텀 역할 삭제 핸들러
  const handleRemoveCustomRole = useCallback(
    (roleToRemove: string) => {
      if (!editingUser) return;

      const roles = editingUser.roles?.filter((role) => role !== roleToRemove) || [];
      // 최소 1개 역할 유지
      if (roles.length > 0) {
        setEditingUser((prev) => (prev ? { ...prev, roles } : null));
        ToastManager.show({
          content: `"${roleToRemove}" 역할이 삭제되었습니다.`,
          duration: 2000,
        });
      } else {
        ToastManager.show({
          content: `최소 1개의 역할은 유지되어야 합니다.`,
          duration: 2000,
        });
      }
    },
    [editingUser]
  );

  // 프롬프트 토글 핸들러
  const handlePromptToggle = useCallback(
    (promptType: PromptType) => {
      if (editingUser) {
        const prompts = [...(editingUser.prompts || [])];
        const promptIndex = prompts.indexOf(promptType);

        if (promptIndex === -1) {
          // 프롬프트 추가
          prompts.push(promptType);
        } else {
          // 프롬프트 제거
          prompts.splice(promptIndex, 1);
        }

        setEditingUser((prev) => (prev ? { ...prev, prompts } : null));
      }
    },
    [editingUser]
  );

  // 비밀번호 옵션 변경 핸들러
  const handlePasswordTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPasswordType(e.target.value as "auto" | "manual");
  };

  // 수동 비밀번호 입력 핸들러
  const handleManualPasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setManualPassword(e.target.value);
  }, []);

  // 자동 생성된 비밀번호 계산
  const getAutoGeneratedPassword = useCallback(() => {
    if (!editingUser?.phone) return "ymyd0000";

    // 전화번호의 마지막 4자리 추출
    const phoneNumber = editingUser.phone.replace(/[^0-9]/g, "");
    const last4Digits = phoneNumber.slice(-4);

    return `ymyd${last4Digits}`;
  }, [editingUser?.phone]);

  // 최종 비밀번호 반환
  const getFinalPassword = useCallback(() => {
    if (passwordType === "auto") {
      return getAutoGeneratedPassword();
    } else if (passwordType === "manual") {
      return manualPassword;
    }
    return "";
  }, [passwordType, manualPassword, getAutoGeneratedPassword]);

  // 저장 핸들러
  const handleSave = useCallback(async () => {
    if (editingUser) {
      try {
        setIsSaving(true);
        // 대기 팝업 표시
        WaitingManager.show({
          message: isNewUser
            ? "사용자를 생성하는 중입니다..."
            : "사용자 정보를 저장하는 중입니다...",
        });

        // 새 사용자인 경우 비밀번호 추가
        const userToSave = { ...editingUser };

        // 새 사용자 생성 시 비밀번호 추가
        if (isNewUser && (passwordType === "auto" || passwordType === "manual")) {
          userToSave.password = getFinalPassword();
        }

        // 새 사용자 또는 기존 사용자 업데이트
        const updatedUser = await saveUser(userToSave);

        // 업데이트된 사용자 정보 전달
        if (onUpdate) {
          onUpdate(userToSave);
        }

        // 선택 상태 업데이트 (선택 컴포넌트를 사용하는 경우)
        if (onSelect) {
          onSelect(userToSave);
        }

        setIsEditing(false);
        setEditingUser(null);
        setIsSaving(false);
        setPasswordType("auto");
        setManualPassword("");

        // 대기 팝업 숨기기
        WaitingManager.hide();

        // 성공 토스트 메시지 표시
        ToastManager.show({
          content: isNewUser
            ? `${userToSave.name} 사용자가 생성되었습니다.`
            : `${userToSave.name} 사용자 정보가 수정되었습니다.`,
          duration: 3000,
        });
      } catch (error) {
        log(LogLevel.ERROR, "사용자 저장 실패:", error);
        // 에러 토스트 메시지 표시
        ToastManager.show({
          content: "사용자 저장에 실패했습니다.",
          duration: 3000,
        });
        WaitingManager.hide();
        setIsSaving(false);
      }
    }
  }, [editingUser, isNewUser, passwordType, getFinalPassword, onUpdate, onSelect]);

  // 역할에 대한 사용자 친화적 이름 가져오기
  const getRoleKoreanName = (role: string): string => {
    return ROLE_NAMES[role] || role;
  };

  // 사용자가 없는 경우
  if (!user) {
    return (
      <Container>
        <EmptyState>
          <Icon name="User" size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
          <Text textType="H3">선택된 사용자가 없습니다</Text>
          <Text textType="Body2" style={{ marginTop: 8 }}>
            왼쪽에서 사용자를 선택하거나 새 사용자를 추가하세요.
          </Text>
        </EmptyState>
      </Container>
    );
  }

  // 편집 모드 UI
  if (isEditing && editingUser) {
    return (
      <Container>
        <Header>
          <div>
            <Text textType="H3" color={COLOR_SYSTEM.get("Blue")?.[700]}>
              {isNewUser ? "사용자 추가" : "사용자 편집"}
            </Text>
            {!isNewUser && editingUser.id && (
              <Text
                textType="Body2"
                color={COLOR_SYSTEM.get("Gray")?.[600]}
                style={{ marginTop: 4 }}
              >
                ID: {editingUser.id}
              </Text>
            )}
          </div>
        </Header>

        <EditContent>
          <FormGroup>
            <Label>이름</Label>
            <Input
              value={editingUser.name || ""}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder="사용자 이름"
            />
          </FormGroup>

          <FormGroup>
            <Label>이메일</Label>
            <Input
              value={editingUser.email || ""}
              onChange={(e) => handleInputChange("email", e.target.value)}
              placeholder="user@example.com"
              type="email"
            />
          </FormGroup>

          <FormGroup>
            <Label>전화번호</Label>
            <Input
              value={editingUser.phone || ""}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              placeholder="010-0000-0000"
            />
          </FormGroup>

          <FormGroup>
            <Label>회사/기관</Label>
            <Input
              value={editingUser.company || ""}
              onChange={(e) => handleInputChange("company", e.target.value)}
              placeholder="회사 또는 기관명"
            />
          </FormGroup>

          <FormGroup>
            <Label>라이센스</Label>
            <Input
              value={editingUser.license || ""}
              onChange={(e) => handleInputChange("license", e.target.value)}
              placeholder="라이센스 정보"
            />
          </FormGroup>

          <FormGroup>
            <Label>약문약답 ID</Label>
            <Input
              type="number"
              value={editingUser.ymydId > 0 ? String(editingUser.ymydId) : ""}
              onChange={(e) => handleInputChange("ymydId", e.target.value ? Number(e.target.value) : undefined)}
              placeholder="약문약답 사용자 ID"
            />
          </FormGroup>

          <FormGroup>
            <Label>가이드 버전</Label>
            <Input
              value={editingUser.guidedVersion || ""}
              onChange={(e) => handleInputChange("guidedVersion", e.target.value)}
              placeholder="가이드 버전"
            />
          </FormGroup>

          <FormGroup>
            <Label>약관 동의 버전</Label>
            <Input
              value={editingUser.agreementVersion || ""}
              onChange={(e) => handleInputChange("agreementVersion", e.target.value)}
              placeholder="약관 동의 버전"
            />
          </FormGroup>

          <FormGroup>
            <Label>메뉴얼 버전</Label>
            <Input
              value={editingUser.manualVersion || ""}
              onChange={(e) => handleInputChange("manualVersion", e.target.value)}
              placeholder="메뉴얼 버전"
            />
          </FormGroup>

          <FormGroup>
            <Label>역할</Label>

            {/* Preset 역할 버튼 그룹 */}
            <div>
              <Text
                textType="Body2"
                color={COLOR_SYSTEM.get("Gray")?.[600]}
                style={{ marginBottom: 8 }}
              >
                Preset 역할
              </Text>
              <RoleSelector>
                {Object.values(UserRole).map((role) => (
                  <RoleOption
                    key={role}
                    isSelected={editingUser.roles?.includes(role) || false}
                    onClick={() => handleRoleToggle(role)}
                  >
                    {getRoleKoreanName(role)}
                  </RoleOption>
                ))}
              </RoleSelector>
            </div>

            {/* 커스텀 역할 표시 영역 */}
            {customRoles.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Text
                  textType="Body2"
                  color={COLOR_SYSTEM.get("Gray")?.[600]}
                  style={{ marginBottom: 8 }}
                >
                  커스텀 역할
                </Text>
                <CustomRoleTags>
                  {customRoles.map((role) => (
                    <CustomRoleTag key={role}>
                      <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                        {role}
                      </Text>
                      <Icon
                        name="X"
                        size={12}
                        color={COLOR_SYSTEM.get("Gray")?.[500]}
                        onClick={() => handleRemoveCustomRole(role)}
                      />
                    </CustomRoleTag>
                  ))}
                </CustomRoleTags>
              </div>
            )}

            {/* 커스텀 역할 추가 입력 */}
            <div style={{ marginTop: 16 }}>
              <Text
                textType="Body2"
                color={COLOR_SYSTEM.get("Gray")?.[600]}
                style={{ marginBottom: 8 }}
              >
                커스텀 역할 추가
              </Text>
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <Input
                    value={customRoleInput}
                    onChange={(e) => setCustomRoleInput(e.target.value)}
                    placeholder="예: 서울시약사회/강남구"
                  />
                </div>
                <Button
                  buttonType="button"
                  size="Medium"
                  variant="Outlined"
                  color="Primary"
                  type="Text"
                  onClick={handleAddCustomRole}
                  disabled={!customRoleInput.trim()}
                >
                  추가
                </Button>
              </div>
            </div>
          </FormGroup>

          <FormGroup>
            <Label>사용 가능한 프롬프트</Label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
              {[
                PromptType.MEDICATION_COUNSELING,
                PromptType.CMM_2,
                PromptType.LCMM,
                PromptType.FREE_QUESTION,
              ].map((promptType) => (
                <ToggleButton
                  key={promptType}
                  type="Text"
                  color="Blue"
                  variant="OutlinedTinted"
                  size="Medium"
                  active={editingUser.prompts?.includes(promptType) || false}
                  onClick={() => handlePromptToggle(promptType)}
                >
                  {getPromptTypeKoreanName(promptType)}
                </ToggleButton>
              ))}
            </div>
          </FormGroup>

          {/* 비밀번호 섹션 - 새 사용자 추가 시에만 표시 */}
          {isNewUser && (
            <FormGroup>
              <Label>비밀번호 설정</Label>
              <RadioGroup>
                <RadioButton
                  value="auto"
                  checked={passwordType === "auto"}
                  onChange={handlePasswordTypeChange}
                  size="Medium"
                >
                  전화번호 기반 자동 생성
                </RadioButton>
                <RadioButton
                  value="manual"
                  checked={passwordType === "manual"}
                  onChange={handlePasswordTypeChange}
                  size="Medium"
                >
                  직접 입력
                </RadioButton>
              </RadioGroup>

              {passwordType === "manual" && (
                <Input
                  type="password"
                  value={manualPassword}
                  onChange={handleManualPasswordChange}
                  placeholder="비밀번호 입력"
                />
              )}
            </FormGroup>
          )}
        </EditContent>

        <Actions>
          <Button
            buttonType="button"
            color="Secondary"
            size="Medium"
            type="Text"
            variant="Outlined"
            onClick={handleCancelEdit}
          >
            취소
          </Button>
          <Button
            buttonType="button"
            color="Primary"
            size="Medium"
            type="Text"
            variant="Contained"
            onClick={handleSave}
            disabled={isSaving || (passwordType === "manual" && manualPassword.length < 1)}
          >
            {isSaving ? "저장 중..." : "저장"}
          </Button>
        </Actions>
      </Container>
    );
  }

  // 상세 정보 보기 UI
  return (
    <Container>
      <Header>
        <div>
          <Text textType="H3" color={COLOR_SYSTEM.get("Blue")?.[700]}>
            사용자 상세
          </Text>
        </div>
        <ButtonGroup>
          <Button
            buttonType="button"
            color="Primary"
            size="Medium"
            type="IconWithText"
            variant="Outlined"
            icon="Edit"
            left
            onClick={() => onResetPassword?.(user)}
          >
            비밀번호 변경
          </Button>
          <Button
            buttonType="button"
            color="Primary"
            size="Medium"
            type="IconWithText"
            variant="Outlined"
            icon="Edit"
            left
            onClick={handleStartEdit}
          >
            수정
          </Button>
          <Button
            buttonType="button"
            color="Negative"
            size="Medium"
            type="IconWithText"
            variant="Outlined"
            icon="Trash"
            left
            onClick={handleOpenDeleteModal}
          >
            삭제
          </Button>
        </ButtonGroup>
      </Header>

      <Content>
        <InfoSection>
          <SectionHeader>
            <Text textType="H4">기본 정보</Text>
          </SectionHeader>

          <Meta>
            <MetaItem>
              <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                ID:
              </Text>
              <Text textType="Body2" style={{ fontWeight: 600 }}>
                {user.id ? user.id.substring(0, 8) + "..." : "새 사용자"}
              </Text>
            </MetaItem>

            <MetaItem>
              <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                등록일:
              </Text>
              <Text textType="Body2" style={{ fontWeight: 600 }}>
                {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "등록 정보 없음"}
              </Text>
            </MetaItem>

            <MetaItem>
              <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                마지막 로그인:
              </Text>
              <Text textType="Body2" style={{ fontWeight: 600 }}>
                {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : "로그인 기록 없음"}
              </Text>
            </MetaItem>
          </Meta>
        </InfoSection>

        <InfoSection>
          <InfoItem>
            <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
              이름
            </Text>
            <Text textType="Body1" style={{ fontWeight: 600 }}>
              {user.name}
            </Text>
          </InfoItem>

          <InfoItem>
            <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
              이메일
            </Text>
            <Text textType="Body1" style={{ fontWeight: 600 }}>
              {user.email}
            </Text>
          </InfoItem>

          {user.phone && (
            <InfoItem>
              <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                전화번호
              </Text>
              <Text textType="Body1" style={{ fontWeight: 600 }}>
                {user.phone}
              </Text>
            </InfoItem>
          )}

          {user.company && (
            <InfoItem>
              <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                회사/기관
              </Text>
              <Text textType="Body1" style={{ fontWeight: 600 }}>
                {user.company}
              </Text>
            </InfoItem>
          )}

          {user.license && (
            <InfoItem>
              <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                면허번호
              </Text>
              <Text textType="Body1" style={{ fontWeight: 600 }}>
                {user.license}
              </Text>
            </InfoItem>
          )}

          {user.ymydId > 0 && (
            <InfoItem>
              <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                약문약답 ID
              </Text>
              <Text textType="Body1" style={{ fontWeight: 600 }}>
                {user.ymydId}
              </Text>
            </InfoItem>
          )}

          {user.guidedVersion && (
            <InfoItem>
              <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                가이드 버전
              </Text>
              <Text textType="Body1" style={{ fontWeight: 600 }}>
                {user.guidedVersion}
              </Text>
            </InfoItem>
          )}

          {user.agreementVersion && (
            <InfoItem>
              <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                약관 동의 버전
              </Text>
              <Text textType="Body1" style={{ fontWeight: 600 }}>
                {user.agreementVersion}
              </Text>
            </InfoItem>
          )}

          {user.manualVersion && (
            <InfoItem>
              <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                메뉴얼 버전
              </Text>
              <Text textType="Body1" style={{ fontWeight: 600 }}>
                {user.manualVersion}
              </Text>
            </InfoItem>
          )}

          <InfoItem>
            <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
              역할
            </Text>
            <RoleTags>
              {user.roles?.map((role) => (
                <Tag
                  key={role}
                  color={
                    role === UserRole.ADMIN ? "Purple" :
                    role === UserRole.PREMIUM ? "Orange" :
                    role === UserRole.GYEONGGI_PHARMACIST ? "Orange" :
                    role === UserRole.SEOUL_PHARMACIST ? "Orange" :
                    "Blue"
                  }
                  variant="Tinted"
                  size="Small"
                >
                  {getRoleKoreanName(role)}
                </Tag>
              ))}
            </RoleTags>
          </InfoItem>

          {user.prompts && user.prompts.length > 0 && (
            <InfoItem>
              <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                사용 가능한 프롬프트
              </Text>
              <RoleTags>
                {user.prompts.map((promptType) => (
                  <Tag
                    key={promptType}
                    color="Green"
                    variant="Tinted"
                    size="Small"
                  >
                    {getPromptTypeKoreanName(promptType)}
                  </Tag>
                ))}
              </RoleTags>
            </InfoItem>
          )}
        </InfoSection>

        {/* 포인트 정보 섹션 */}
        {!isNewUser && (
          <InfoSection>
            <SectionHeader>
              <Text textType="H4">포인트 정보</Text>
            </SectionHeader>

            <PointsCard>
              <PointsGrid>
                <PointsItem>
                  <Text textType="H3" color={COLOR_SYSTEM.get("Blue")?.[600]}>
                    {userPoints?.currentPoints?.toLocaleString() || 0}P
                  </Text>
                  <Text textType="Body2Medium" color={COLOR_SYSTEM.get("Gray")?.[600]}>
                    현재 포인트
                  </Text>
                </PointsItem>
                <PointsItem>
                  <Text textType="H3" color={COLOR_SYSTEM.get("Green")?.[600]}>
                    {userPoints?.totalEarnedPoints?.toLocaleString() || 0}P
                  </Text>
                  <Text textType="Body2Medium" color={COLOR_SYSTEM.get("Gray")?.[600]}>
                    총 적립
                  </Text>
                </PointsItem>
                <PointsItem>
                  <Text textType="H3" color={COLOR_SYSTEM.get("Red")?.[600]}>
                    {userPoints?.totalUsedPoints?.toLocaleString() || 0}P
                  </Text>
                  <Text textType="Body2Medium" color={COLOR_SYSTEM.get("Gray")?.[600]}>
                    총 사용
                  </Text>
                </PointsItem>
              </PointsGrid>

              {/* 포인트 지급 폼 */}
              <GrantPointsForm>
                <div style={{ flex: 1 }}>
                  <Input
                    placeholder="지급할 포인트"
                    value={grantPointsAmount}
                    onChange={(e) => setGrantPointsAmount(e.target.value)}
                    type="number"
                  />
                </div>
                <div style={{ flex: 2 }}>
                  <Input
                    placeholder="지급 사유 (선택사항)"
                    value={grantDescription}
                    onChange={(e) => setGrantDescription(e.target.value)}
                  />
                </div>
                <Button
                  onClick={handleGrantPoints}
                  disabled={isGrantingPoints || !grantPointsAmount}
                  size="Medium"
                  type="Text"
                  variant="Contained"
                  color="Primary"
                >
                  {isGrantingPoints ? "지급 중..." : "포인트 지급"}
                </Button>
              </GrantPointsForm>
            </PointsCard>

            {/* 포인트 거래 내역 */}
            <div style={{ marginTop: "16px" }}>
              <Text textType="Body1Medium" style={{ marginBottom: "8px" }}>
                최근 거래 내역
              </Text>
              {isLoadingPoints ? (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <Text textType="Body2Medium" color={COLOR_SYSTEM.get("Gray")?.[500]}>
                    로딩 중...
                  </Text>
                </div>
              ) : pointsTransactions.length > 0 ? (
                <TransactionsList>
                  {pointsTransactions.map((transaction) => (
                    <TransactionItem key={transaction.id}>
                      <TransactionInfo>
                        <Text textType="Body2Medium" style={{ marginBottom: "4px" }}>
                          {transaction.description || "포인트 거래"}
                        </Text>
                        <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[600]}>
                          {new Date(transaction.createdAt).toLocaleDateString("ko-KR", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </Text>
                      </TransactionInfo>
                      <TransactionAmount isPositive={transaction.points > 0}>
                        {transaction.points > 0 ? "+" : ""}
                        {transaction.points.toLocaleString()}P
                      </TransactionAmount>
                    </TransactionItem>
                  ))}
                </TransactionsList>
              ) : (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <Text textType="Body2Medium" color={COLOR_SYSTEM.get("Gray")?.[500]}>
                    포인트 거래 내역이 없습니다
                  </Text>
                </div>
              )}
            </div>
          </InfoSection>
        )}
      </Content>
    </Container>
  );
};

export default UserInfo;
